import {
  Children,
  isValidElement,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type GalleryItem = {
  id: string;
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  badge?: ReactNode;
  icon?: string;
  iconTone?: 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'secondary';
  actions?: ReactNode;
};

type ViewMode = 'table' | 'gallery';

type Props = {
  columns: string[];
  children: ReactNode;
  refreshKey: string | number;
  pageLength?: number;
  order?: Array<[number, 'asc' | 'desc']>;
  className?: string;
  actionsColumn?: boolean;
  storageKey?: string;
  galleryItems?: GalleryItem[];
  toolbar?: ReactNode;
};

function readMode(key?: string): ViewMode {
  if (!key || typeof window === 'undefined') return 'table';
  try {
    const v = localStorage.getItem(`pk-view:${key}`);
    return v === 'gallery' ? 'gallery' : 'table';
  } catch {
    return 'table';
  }
}

function nodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join(' ');
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return nodeText(node.props.children);
  }
  return '';
}

/**
 * Table / gallery list view with client-side search + pagination.
 * Pure React — no jQuery DataTables (avoids empty tables / removeChild crashes).
 */
export function ParkingDataTable({
  columns,
  children,
  refreshKey,
  pageLength = 10,
  className = '',
  actionsColumn,
  storageKey,
  galleryItems,
  toolbar,
}: Props) {
  const [mode, setMode] = useState<ViewMode>(() => readMode(storageKey));
  const [query, setQuery] = useState('');
  const [length, setLength] = useState(pageLength);
  const [page, setPage] = useState(1);

  const lastCol = columns[columns.length - 1] ?? '';
  const withActions =
    actionsColumn === true ||
    (actionsColumn !== false && /^(acciones|actions)$/i.test(lastCol.trim()));

  const setView = (next: ViewMode) => {
    if (next === mode) return;
    setMode(next);
    if (storageKey) {
      try {
        localStorage.setItem(`pk-view:${storageKey}`, next);
      } catch {
        // ignore
      }
    }
  };

  const rows = useMemo(() => {
    void refreshKey;
    return Children.toArray(children).filter((child) => isValidElement(child));
  }, [children, refreshKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => nodeText(row).toLowerCase().includes(q));
  }, [rows, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / length) || 1);
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * length;
  const pageRows = filtered.slice(start, start + length);
  const end = filtered.length === 0 ? 0 : Math.min(start + pageRows.length, filtered.length);

  const items = useMemo(() => galleryItems ?? [], [galleryItems]);

  const filteredGallery = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const blob = [nodeText(item.title), nodeText(item.subtitle), nodeText(item.meta)]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [items, query]);

  return (
    <div className={`data-view data-view--${mode}`}>
      <div className="data-view__bar">
        <div className="btn-group view-toggle" role="group" aria-label="Vista">
          <button
            type="button"
            className={`btn btn-sm ${mode === 'table' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setView('table')}
            title="Vista tabla"
          >
            <i className="bi bi-table me-1" />
            <span className="d-none d-sm-inline">Tabla</span>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${mode === 'gallery' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setView('gallery')}
            title="Vista galería"
          >
            <i className="bi bi-grid-3x3-gap me-1" />
            <span className="d-none d-sm-inline">Galería</span>
          </button>
        </div>

        {toolbar ? <div className="data-view__extra">{toolbar}</div> : null}

        <div className="data-view__tools">
          <select
            className="form-select form-select-sm data-view__length"
            value={length}
            aria-label="Filas"
            onChange={(e) => {
              setLength(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <input
            type="search"
            className="form-control form-control-sm data-view__search"
            placeholder="Buscar…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {mode === 'table' ? (
        <>
          <div className="table-responsive data-view__table-wrap">
            <table
              className={`table table-row-bordered table-pk align-middle gy-3 gs-3 w-100 ${withActions ? 'table-pk--with-actions' : ''} ${className}`.trim()}
            >
              <thead>
                <tr>
                  {columns.map((c, i) => (
                    <th
                      key={`${c}-${i}`}
                      className={withActions && i === columns.length - 1 ? 'table-pk__actions-col' : undefined}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(columns.length, 1)} className="text-center text-muted py-4">
                      Sin datos
                    </td>
                  </tr>
                ) : (
                  pageRows
                )}
              </tbody>
            </table>
          </div>
          <div className="data-view__footer">
            <div className="data-view__info text-muted small">
              {filtered.length === 0
                ? 'Sin datos'
                : `${start + 1}–${end} de ${filtered.length}`}
            </div>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={safePage <= 1}
                onClick={() => setPage(1)}
              >
                «
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              <button type="button" className="btn btn-sm btn-light" disabled>
                {safePage}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={safePage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                disabled={safePage >= pageCount}
                onClick={() => setPage(pageCount)}
              >
                »
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="gallery-grid">
          {filteredGallery.length === 0 ? (
            <div className="alert alert-info mb-0">Sin datos</div>
          ) : (
            <div className="row g-3">
              {filteredGallery.map((item) => (
                <div className="col-12 col-sm-6 col-lg-4 col-xxl-3" key={item.id}>
                  <div className="gallery-card">
                    <div className="gallery-card__top">
                      <span className={`symbol symbol-${item.iconTone ?? 'primary'}`}>
                        <i className={`bi ${item.icon ?? 'bi-card-text'}`} />
                      </span>
                      {item.badge ? <div>{item.badge}</div> : null}
                    </div>
                    <div className="gallery-card__title">{item.title}</div>
                    {item.subtitle ? (
                      <div className="gallery-card__subtitle">{item.subtitle}</div>
                    ) : null}
                    {item.meta ? <div className="gallery-card__meta">{item.meta}</div> : null}
                    {item.actions ? (
                      <div className="gallery-card__actions">{item.actions}</div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
